import { injectStrict, mapKeys } from "@/utils/utils";
import type { MaybeRef } from "@vueuse/core";
import type { Ref } from "vue";
import { ref, computed } from "vue";
import type {
  Correlations,
  DeviationSurvey,
  Equipment,
  OperatingParameters,
  Scenario,
  WellProperties,
  XrefCables,
  XrefHousings,
  XrefIntakes,
  XrefPumps,
  XrefSeals,
  XrefYToolBypassTubings,
  XrefYToolEquipmentTubings
} from "./forms/formTypes";
import {
  correlationsFormKey,
  equipmentFormKey,
  parametersFormKey,
  wellPropertiesFormKey,
  deviationSurveyFormKey,
  sealsFormKey,
  intakesFormKey,
  cablesFormKey,
  pumpsFormKey,
  housingsFormKey,
  yToolBypassTubingsFormKey,
  yToolEquipmentTubingsFormKey,
  scenarioFormKey
} from "./scenarioDetailProvider/scenarioDetailProviderKeys";

// Re-export generic y-tool types from formTypes for convenience
export type {
  XrefYToolBypassTubings,
  XrefYToolEquipmentTubings
} from "./forms/formTypes";

export type DestructuredScenario = {
  scenario: Ref<Scenario | null | undefined>;
  correlations: Ref<Correlations | null | undefined>;
  equipment: Ref<Equipment | null | undefined>;
  parameters: Ref<OperatingParameters | null | undefined>;
  wellProperties: Ref<WellProperties | null | undefined>;
  deviationSurvey: Ref<DeviationSurvey | null | undefined>;
  intakes: Ref<Record<string, XrefIntakes[number]>>;
  pumps: Ref<Record<string, XrefPumps[number]>>;
  housings: Ref<Record<string, XrefHousings[number]>>;
  cables: Ref<Record<string, XrefCables[number]>>;
  seals: Ref<Record<string, XrefSeals[number]>>;
  yToolBypassTubings: Ref<Record<string, XrefYToolBypassTubings[number]>>;
  yToolEquipmentTubings: Ref<Record<string, XrefYToolEquipmentTubings[number]>>;
};

export default function (
  scenario?: MaybeRef<Scenario | null | undefined>
): DestructuredScenario {
  if (scenario) {
    const _scenario = ref(scenario);

    const correlations = computed(
      () => _scenario.value?.correlations as Correlations
    );
    const equipment = computed(() => _scenario.value?.equipment);
    const parameters = computed(() => _scenario.value?.parameters);
    const wellProperties = computed(
      () => _scenario.value?.well_properties as WellProperties
    );
    const deviationSurvey = computed(
      () => wellProperties.value?.r_deviation_survey
    );
    const intakes = computed(() =>
      mapKeys(equipment.value?.r_xref_intakes || [], item => item.id)
    );
    const pumps = computed(() =>
      mapKeys(equipment.value?.r_xref_pumps || [], item => item.id)
    );
    const housings = computed(() =>
      mapKeys(
        equipment.value?.r_xref_pumps.reduce(
          (a, b) => [...a, ...b.r_xref_housings],
          [] as XrefHousings
        ) || [],
        item => item.id
      )
    );
    const cables = computed(() =>
      mapKeys(equipment.value?.r_xref_cables || [], item => item.id)
    );
    const seals = computed(() =>
      mapKeys(equipment.value?.r_xref_seals || [], item => item.id)
    );
    const yToolBypassTubings = computed(() => {
      // Combine uphole and downhole tubings, filtering out null ids
      const allTubings = [
        ...(equipment.value?.r_xref_uphole_y_tool_bypass_tubings || [])
        // ...(equipment.value?.r_xref_downhole_y_tool_bypass_tubings || [])
      ].filter(item => item.id != null);
      return mapKeys(allTubings, item => item.id as string) as Record<
        string,
        XrefYToolBypassTubings[number]
      >;
    });
    const yToolEquipmentTubings = computed(() => {
      // Combine uphole and downhole tubings, filtering out null ids
      const allTubings = [
        ...(equipment.value?.r_xref_uphole_y_tool_equipment_tubings || [])
        // ...(equipment.value?.r_xref_downhole_y_tool_equipment_tubings || [])
      ].filter(item => item.id != null);
      return mapKeys(allTubings, item => item.id as string) as Record<
        string,
        XrefYToolEquipmentTubings[number]
      >;
    });

    return {
      scenario: _scenario,
      correlations,
      equipment,
      parameters,
      wellProperties,
      deviationSurvey,
      intakes,
      pumps,
      housings,
      cables,
      seals,
      yToolBypassTubings,
      yToolEquipmentTubings
    };
  }

  const correlationsForm = injectStrict(correlationsFormKey);
  const equipmentForm = injectStrict(equipmentFormKey);
  const parametersForm = injectStrict(parametersFormKey);
  const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
  const deviationSurveyForm = injectStrict(deviationSurveyFormKey);
  const sealsForm = injectStrict(sealsFormKey);
  const intakesForm = injectStrict(intakesFormKey);
  const cablesForm = injectStrict(cablesFormKey);
  const pumpsForm = injectStrict(pumpsFormKey);
  const housingsForm = injectStrict(housingsFormKey);
  const yToolBypassTubingsForm = injectStrict(yToolBypassTubingsFormKey);
  const yToolEquipmentTubingsForm = injectStrict(yToolEquipmentTubingsFormKey);
  const scenarioForm = injectStrict(scenarioFormKey);

  return {
    correlations: correlationsForm.data,
    equipment: equipmentForm.data,
    parameters: parametersForm.data,
    wellProperties: wellPropertiesForm.data,
    deviationSurvey: deviationSurveyForm.data,
    seals: sealsForm.data,
    intakes: intakesForm.data,
    cables: cablesForm.data,
    pumps: pumpsForm.data,
    housings: housingsForm.data,
    yToolBypassTubings: yToolBypassTubingsForm.data,
    yToolEquipmentTubings: yToolEquipmentTubingsForm.data,
    scenario: scenarioForm.data
  };
}
